'use client';

import type { MouseEventHandler } from 'react';
import React, { useCallback, useEffect } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { Typography, Button, List } from 'antd';
import { IoArrowForward } from 'react-icons/io5';
import { MdCancel } from 'react-icons/md';

import { useAuthGuard } from '@/hooks/useAuthGuard';

type SubItem = {
  id: string | number;
  name: string;
  description?: string | null;
  requires_auth?: boolean;
};

interface ServiceSubOverlayProps {
  open: boolean;
  onClose: () => void;
  labelId: string;
  serviceId: string | number;
  title: string;
  subproducts: SubItem[];
}

const ServiceSubOverlay: React.FC<ServiceSubOverlayProps> = ({
  open,
  onClose,
  labelId,
  serviceId,
  title,
  subproducts,
}) => {
  const router = useRouter();

  const { guard } = useAuthGuard({
    loginPath: '/account/signin',
    onRedirect: onClose,
  });

  const buildApplicationHref = (
    subId: string | number,
    requiresAuth?: boolean
  ) => {
    const base = `/services/application/${serviceId}/i/${serviceId}/${subId}`;
    return requiresAuth ? `${base}?requiresCustomerDetails=1` : base;
  };

  useEffect(() => {
    if (!open) return;

    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  const stop: MouseEventHandler<HTMLElement> = useCallback((e) => {
    e.preventDefault?.();
    e.stopPropagation();
  }, []);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelId}
      onClick={stop}
      onMouseDown={stop}
      onPointerDown={stop}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 40,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 16,
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden="true"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.18)',
          backdropFilter: 'blur(6px)',
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          background: '#fff',
          border: '1px solid #e5e7eb',
          boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
          borderRadius: 16,
        }}
        onClick={stop}
        onMouseDown={stop}
        onPointerDown={stop}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 10px',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Typography.Title
            level={5}
            style={{ margin: 0, fontWeight: 600 }}
            title={title}
          >
            {title}
          </Typography.Title>

          <Button
            type="text"
            aria-label="Close"
            icon={<MdCancel size={20} />}
            style={{ color: '#6b7280' }}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 0 }}>
          {subproducts.length === 0 ? (
            <Typography.Text type="secondary" style={{ padding: 16 }}>
              No sub-services are available at this time.
            </Typography.Text>
          ) : (
            <List
              itemLayout="horizontal"
              dataSource={subproducts}
              renderItem={(sub) => {
                const href = buildApplicationHref(sub.id, sub.requires_auth);

                const onItemClick: MouseEventHandler<HTMLAnchorElement> = (
                  e
                ) => {
                  e.preventDefault();
                  e.stopPropagation();

                  if (guard(!!sub.requires_auth, href)) return;

                  onClose();
                  router.push(href);
                };

                return (
                  <List.Item
                    style={{ padding: 0, borderBottom: '1px solid #f5f5f5' }}
                  >
                    <Link
                      href={href}
                      onClick={onItemClick}
                      role="menuitem"
                      tabIndex={0}
                      className="
    w-full
    flex items-start gap-3
    px-4 py-3
    cursor-pointer
    transition-colors duration-150
    hover:bg-[#F4F9FF]
  "
                      title={sub.description || sub.name}
                      style={{ textDecoration: 'none' }}
                    >
                      <div className="flex-1 min-w-0">
                        <Typography.Text
                          strong
                          className="
        block text-[14px]
        text-[#102A43]
        transition-colors duration-150
        hover:text-[#7FB0F3]!
      "
                        >
                          {sub.name}
                        </Typography.Text>

                        {sub.description && (
                          <Typography.Text
                            className="
          mt-0.5 block text-[12px]
          text-[#4A5563]
          transition-colors duration-150
          hover:text-[#7FB0F3]!
        "
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {sub.description}
                          </Typography.Text>
                        )}
                      </div>

                      <IoArrowForward
                        className="
      mt-0.5
      text-[#7FB0F3]
      opacity-60
      transition-transform duration-150
    "
                      />
                    </Link>
                  </List.Item>
                );
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceSubOverlay;
